// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useRef } from "react";
import type { JSX } from "react";
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from "@fluentui/react-components";

import { useAuthSession } from "../../hooks/useAuthSession";
import { SIGN_IN_AGAIN_LABEL } from "../common/signedOut/signedOutCopy";
import { useSignInAgain } from "../common/signedOut/useSignInAgain";
import {
  notifyAuthTokenAvailable,
  onAuthTokenUnavailable,
} from "../../services/api/demoData";
import {
  isSessionExpired,
  notifySessionExpired,
  useSessionExpiredSignal,
} from "../../utils/sessionExpirySignal";

let mountedBanners = 0;

/**
 * True while the app shell (and so this bar) is on screen. ProtectedRoute
 * keeps the page through a mid-work expiry only then; an expiry before the
 * product ever showed still returns to sign-in.
 */
export function isSignedOutBannerMounted(): boolean {
  return mountedBanners > 0;
}

/**
 * The one app-level signed-out state (RiddleDesign ruling on issue 910): a
 * persistent, deduped warning bar with "Sign In Again" that returns the user
 * to this page. It shows on the first 401, or when a client had no token and
 * the session really cannot issue one (a token that is only not ready yet
 * never shows it).
 */
export function SignedOutBanner(): JSX.Element | null {
  const isSignedOut = useSessionExpiredSignal();
  const { getAccessToken } = useAuthSession();
  const signInAgain = useSignInAgain();
  const isCheckingRef = useRef(false);

  useEffect(() => {
    mountedBanners += 1;
    return () => {
      mountedBanners -= 1;
    };
  }, []);

  useEffect(
    () =>
      onAuthTokenUnavailable(() => {
        if (isCheckingRef.current || isSessionExpired()) {
          return;
        }
        isCheckingRef.current = true;
        void getAccessToken()
          .then(
            () => notifyAuthTokenAvailable(),
            () => notifySessionExpired(),
          )
          .finally(() => {
            isCheckingRef.current = false;
          });
      }),
    [getAccessToken],
  );

  if (!isSignedOut) {
    return null;
  }

  return (
    <MessageBar intent="warning" politeness="assertive" layout="multiline">
      <MessageBarBody>
        <MessageBarTitle>You&apos;re signed out.</MessageBarTitle>
        Sign in again to keep working. Your unsaved changes on this page are kept.
      </MessageBarBody>
      <MessageBarActions>
        <Button appearance="primary" onClick={signInAgain}>
          {SIGN_IN_AGAIN_LABEL}
        </Button>
      </MessageBarActions>
    </MessageBar>
  );
}
